import React, { useState, useEffect, useContext, Fragment } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import { getCurrencySymbol } from '../utils/currencyUtils';
import { formatDate } from '../utils/dateUtils';
import { buildApiUrl } from '../config/environment';
import { Dialog, Transition } from '@headlessui/react';
import {
  XMarkIcon,
  ShieldCheckIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  CurrencyDollarIcon,
  PlusCircleIcon,
  MinusCircleIcon,
  ExclamationTriangleIcon,
  TrashIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';

const BondModal = ({ show, onClose, tenancy, selectedOccupant, onDataUpdate }) => {
  const { agencyCurrency, user, agencyCountry, agencyId } = useContext(AuthContext);

  // Helper function for consistent date formatting
  const formatDateWithContext = (dateString) => {
    return formatDate(dateString, {
      userPreference: user?.dateFormatPreference,
      agencyCountry: agencyCountry
    });
  };

  const [bondTransactions, setBondTransactions] = useState([]);
  const [bondSummary, setBondSummary] = useState(null);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [savingTransaction, setSavingTransaction] = useState(false);
  const [deletingTransaction, setDeletingTransaction] = useState(null);

  // Modal states for deposit and deduction
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [showDeductionModal, setShowDeductionModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [creatingTransfer, setCreatingTransfer] = useState(false);
  const [transferAmount, setTransferAmount] = useState('');

  // Form states for deposit and deduction
  const [depositForm, setDepositForm] = useState({
    amount: '',
    date: new Date().toISOString().split('T')[0],
    reference: '',
    description: ''
  });

  const [deductionForm, setDeductionForm] = useState({
    amount: '',
    date: new Date().toISOString().split('T')[0],
    reason: '',
    description: ''
  });

  const [errors, setErrors] = useState({});

  // Dynamic charge categories from backend
  const [chargeCategories, setChargeCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);

  // Fetch charge categories from API
  useEffect(() => {
    if (!show || !agencyId) return;

    const fetchCategories = async () => {
      setLoadingCategories(true);
      try {
        const token = sessionStorage.getItem('access_token') ||
                     sessionStorage.getItem('token') ||
                     localStorage.getItem('access_token') ||
                     localStorage.getItem('token');

        const response = await fetch(
          buildApiUrl(`/agencies/${agencyId}/charge-categories`),
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        if (response.ok) {
          const data = await response.json();
          setChargeCategories(data || []);
        } else {
          console.error('[BondModal] Failed to fetch charge categories:', response.status);
          setChargeCategories([]);
        }
      } catch (error) {
        console.error('[BondModal] Error fetching charge categories:', error);
        setChargeCategories([]);
      } finally {
        setLoadingCategories(false);
      }
    };

    fetchCategories();
  }, [show, agencyId]);

  useEffect(() => {
    if (show && tenancy) {
      console.log('[BondModal] Modal opened with tenancy:', tenancy);
      fetchBondSummary().then(() => {
        fetchBondTransactions();
      });
    }
  }, [show, tenancy]);


  const fetchBondTransactions = async () => {
    if (!tenancy.bond_ledger_id) {
      console.log('[BondModal] Missing bond_ledger_id for fetchBondTransactions:', {
        tenancyId: tenancy.id,
        bondLedgerId: tenancy.bond_ledger_id
      });
      return;
    }

    setLoadingTransactions(true);
    try {
      const token = sessionStorage.getItem('access_token');
      if (!token) {
        throw new Error('Authentication token not found');
      }

      const url = buildApiUrl(`/bond-ledgers/${tenancy.bond_ledger_id}/entries`);
      console.log('[BondModal] Fetching bond ledger entries from:', url);

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('[BondModal] Bond ledger entries response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[BondModal] Bond ledger entries API error:', errorText);
        throw new Error(`Failed to fetch bond ledger entries: ${response.status}`);
      }

      const responseData = await response.json();
      console.log('[BondModal] Bond ledger entries response received:', responseData);

      // Extract entries from the response
      const entries = responseData.data || responseData.entries || [];
      let transformedTransactions = [];

      // Process entries from the direct API response
      if (entries.length > 0) {
        console.log('[BondModal] Processing entries from API:', entries);

        transformedTransactions = entries.map(entry => {
          // Determine transaction type based on entry_type
          let type = 'deposit'; // default
          if (entry.entry_type === 'charge') {
            type = 'deduction';
          } else if (entry.entry_type === 'payment') {
            type = 'deposit';
          } else if (entry.entry_type === 'transfer') {
            type = 'transfer';
          }

          // Extract amount - handle both old and new formats
          let amount = 0;
          if (entry.amount?.amount) {
            amount = entry.amount.amount / 100; // Convert from cents to dollars
          } else if (typeof entry.amount === 'number') {
            amount = entry.amount;
          }

          console.log('[BondModal] Processing entry:', {
            entryId: entry.id,
            entryType: entry.entry_type,
            rawAmount: entry.amount,
            extractedAmount: amount,
            description: entry.description
          });

          return {
            id: `${entry.entry_type}_${entry.id}`,
            date: entry.entry_date || entry.created_at,
            type: type,
            amount: amount,
            description: entry.description || `${entry.entry_type} transaction`,
            reference: entry.reference || '',
            reason: entry.category || entry.reason || '',
            balance_after: entry.balance_after || 0
          };
        });

        console.log('[BondModal] Transformed entries from API:', transformedTransactions);
      }

      // If no entries from direct API, fallback to bond summary data
      if (transformedTransactions.length === 0 && bondSummary && (bondSummary.Payments || bondSummary.Charges)) {
        console.log('[BondModal] Using fallback data from bond summary');

        // Add payments as deposits
        if (bondSummary.Payments && bondSummary.Payments.length > 0) {
          const payments = bondSummary.Payments.map(payment => {
            const amount = payment.amount?.amount ? (payment.amount.amount / 100) : 0;
            console.log('[BondModal] Processing payment from summary:', {
              paymentId: payment.ID,
              rawAmount: payment.amount,
              extractedAmount: amount
            });
            return {
              id: `payment_${payment.ID}`,
              date: payment.PaymentDate || payment.CreatedAt,
              type: 'deposit',
              amount: amount,
              description: 'Bond Payment',
              reference: payment.Reference || '',
              reason: '',
              balance_after: 0
            };
          });
          transformedTransactions = [...transformedTransactions, ...payments];
        }

        // Add charges as deductions
        if (bondSummary.Charges && bondSummary.Charges.length > 0) {
          const charges = bondSummary.Charges.map(charge => {
            const amount = charge.amount?.amount ? (charge.amount.amount / 100) : 0;
            console.log('[BondModal] Processing charge from summary:', {
              chargeId: charge.ID,
              rawAmount: charge.amount,
              extractedAmount: amount
            });
            return {
              id: `charge_${charge.ID}`,
              date: charge.IncurredAt || charge.CreatedAt,
              type: 'deduction',
              amount: amount,
              description: charge.Note || 'Bond Charge',
              reference: charge.Reference || '',
              reason: charge.category_name || charge.Category || '',
              balance_after: 0
            };
          });
          transformedTransactions = [...transformedTransactions, ...charges];
        }
      }

      // Sort by date descending
      transformedTransactions.sort((a, b) => new Date(b.date) - new Date(a.date));

      console.log('[BondModal] Transformed transactions:', transformedTransactions);
      setBondTransactions(transformedTransactions);
    } catch (error) {
      console.error('Error fetching bond transactions:', error);
      setBondTransactions([]);
    } finally {
      setLoadingTransactions(false);
    }
  };

  const fetchBondSummary = async () => {
    if (!tenancy.bond_ledger_id) {
      console.log('[BondModal] Missing bond_ledger_id for fetchBondSummary:', {
        tenancyId: tenancy.id,
        bondLedgerId: tenancy.bond_ledger_id
      });
      return;
    }

    setLoadingSummary(true);
    try {
      const token = sessionStorage.getItem('access_token');
      if (!token) {
        throw new Error('Authentication token not found');
      }

      const url = buildApiUrl(`/bond-ledgers/${tenancy.bond_ledger_id}`);
      console.log('[BondModal] Fetching bond ledger from:', url);

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('[BondModal] Bond ledger response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[BondModal] Bond ledger API error:', errorText);
        throw new Error(`Failed to fetch bond ledger: ${response.status}`);
      }

      const bondLedger = await response.json();
      console.log('[BondModal] Bond ledger data received:', bondLedger);
      setBondSummary(bondLedger);
    } catch (error) {
      console.error('Error fetching bond summary:', error);
      setBondSummary(null);
    } finally {
      setLoadingSummary(false);
    }
  };

  const handleDepositSubmit = async (e) => {
    e.preventDefault();
    setSavingTransaction(true);
    setErrors({});

    try {
      const token = sessionStorage.getItem('access_token');
      if (!token) {
        throw new Error('Authentication token not found');
      }

      // Prepare payload for bond payment - server handles cents conversion
      const payload = {
        amount: parseFloat(depositForm.amount), // Send as dollars, server converts to cents
        payment_date: depositForm.date,
        description: depositForm.description,
        reference: depositForm.reference || ''
      };

      console.log('[BondModal] Sending bond payment payload:', payload);

      // Call the new bond ledger payments endpoint
      const response = await fetch(
        buildApiUrl(`/bond-ledgers/${tenancy.bond_ledger_id}/payments`),
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        }
      );

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: 'Failed to add bond payment' }));
        throw new Error(error.message || 'Failed to add bond payment');
      }

      const result = await response.json();
      console.log('Bond payment added successfully:', result);

      // Refresh data after successful deposit
      await Promise.all([
        fetchBondTransactions(),
        fetchBondSummary()
      ]);

      // Reset form and close modal
      setDepositForm({
        amount: '',
        date: new Date().toISOString().split('T')[0],
        reference: '',
        description: ''
      });

      setShowDepositModal(false);
      alert('Bond deposit added successfully!');
    } catch (error) {
      console.error('Error creating deposit:', error);
      alert(`Failed to add bond deposit: ${error.message}`);
    } finally {
      setSavingTransaction(false);
    }
  };

  const handleDeductionSubmit = async (e) => {
    e.preventDefault();
    setSavingTransaction(true);
    setErrors({});

    // Validate amount
    const amount = parseFloat(deductionForm.amount);
    if (!deductionForm.amount || isNaN(amount) || amount <= 0) {
      setErrors({ amount: 'Please enter a valid amount' });
      setSavingTransaction(false);
      return;
    }

    if (amount > bondLedgerBalance) {
      setErrors({
        amount: `Deduction amount cannot exceed the current bond balance of ${bondLedgerSymbol}${bondLedgerBalance.toFixed(2)}`
      });
      setSavingTransaction(false);
      return;
    }

    try {
      const token = sessionStorage.getItem('access_token');
      if (!token) {
        throw new Error('Authentication token not found');
      }


      // Prepare payload for bond charge - server handles cents conversion
      // Convert date to ISO datetime format for time.Time field
      const incurredAtDate = new Date(deductionForm.date);
      incurredAtDate.setHours(12, 0, 0, 0); // Set to noon to avoid timezone issues

      const payload = {
        amount: parseFloat(deductionForm.amount), // Send as dollars, server converts to cents
        incurred_at: incurredAtDate.toISOString(), // Convert to ISO datetime string
        category_id: parseInt(deductionForm.reason, 10),
        note: deductionForm.description
      };

      console.log('[BondModal] Sending bond charge payload:', payload);

      // Call the new bond ledger charges endpoint
      const response = await fetch(
        buildApiUrl(`/bond-ledgers/${tenancy.bond_ledger_id}/charges`),
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        }
      );

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: 'Failed to add bond charge' }));
        throw new Error(error.message || 'Failed to add bond charge');
      }

      const result = await response.json();
      console.log('Bond charge added successfully:', result);

      // Refresh data after successful deduction
      await Promise.all([
        fetchBondTransactions(),
        fetchBondSummary()
      ]);

      // Reset form and close modal
      setDeductionForm({
        amount: '',
        date: new Date().toISOString().split('T')[0],
        reason: '',
        description: ''
      });

      setShowDeductionModal(false);
      alert('Bond deduction added successfully!');
    } catch (error) {
      console.error('Error creating deduction:', error);
      alert(`Failed to add bond deduction: ${error.message}`);
    } finally {
      setSavingTransaction(false);
    }
  };

  const handleDeleteTransaction = async (transaction) => {
    // Extract the actual ID and type from the transaction
    const isPayment = transaction.type === 'deposit';
    const isCharge = transaction.type === 'deduction';
    const isTransfer = transaction.type === 'transfer';
    const actualId = transaction.id.replace(/^(payment_|charge_|transfer_)/, '');

    let confirmMessage = '';
    if (isPayment) {
      confirmMessage = `Are you sure you want to delete this deposit of ${bondLedgerSymbol}${transaction.amount.toFixed(2)}?`;
    } else if (isCharge) {
      confirmMessage = `Are you sure you want to delete this deduction of ${bondLedgerSymbol}${transaction.amount.toFixed(2)}?`;
    } else if (isTransfer) {
      confirmMessage = `Are you sure you want to delete this transfer of ${bondLedgerSymbol}${transaction.amount.toFixed(2)}?`;
    }

    if (!window.confirm(confirmMessage)) {
      return;
    }

    setDeletingTransaction(transaction.id);

    try {
      const token = sessionStorage.getItem('access_token');
      if (!token) {
        throw new Error('Authentication token not found');
      }

      // Use the correct endpoint based on transaction type
      let endpoint = '';
      if (isPayment) {
        endpoint = `/bond-ledgers/payments/${actualId}`;
      } else if (isCharge) {
        endpoint = `/bond-ledgers/charges/${actualId}`;
      } else if (isTransfer) {
        endpoint = `/bond-ledgers/transfers/${actualId}`;
      } else {
        throw new Error('Unknown transaction type');
      }

      console.log('[BondModal] Deleting transaction:', {
        transactionId: transaction.id,
        actualId,
        type: transaction.type,
        endpoint
      });

      const response = await fetch(buildApiUrl(endpoint), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: 'Failed to delete transaction' }));
        throw new Error(error.message || 'Failed to delete transaction');
      }

      console.log('[BondModal] Transaction deleted successfully');

      // Refresh data after successful deletion
      await Promise.all([
        fetchBondTransactions(),
        fetchBondSummary()
      ]);

      const transactionTypeLabel = isPayment ? 'deposit' : isCharge ? 'deduction' : 'transfer';
      alert(`Bond ${transactionTypeLabel} deleted successfully!`);
    } catch (error) {
      console.error('Error deleting transaction:', error);
      alert(`Failed to delete transaction: ${error.message}`);
    } finally {
      setDeletingTransaction(null);
    }
  };

  const handleCreateTransfer = async () => {
    // Validate amount
    const amount = parseFloat(transferAmount);
    if (!transferAmount || isNaN(amount) || amount <= 0) {
      alert('Please enter a valid transfer amount');
      return;
    }

    if (amount > bondLedgerBalance) {
      alert(`Transfer amount cannot exceed the current bond balance of ${bondLedgerSymbol}${bondLedgerBalance.toFixed(2)}`);
      return;
    }

    // Show confirmation dialog
    const confirmed = window.confirm(
      `Are you sure you want to transfer ${bondLedgerSymbol}${amount.toFixed(2)} from the bond to pay rent?\n\n` +
      'This action will be recorded in the transaction history.'
    );

    if (!confirmed) {
      return;
    }

    setCreatingTransfer(true);

    try {
      const token = sessionStorage.getItem('access_token');
      if (!token) {
        throw new Error('Authentication token not found');
      }

      const payload = {
        amount: amount // Send as dollars, server converts to cents
      };

      console.log('[BondModal] Creating bond transfer:', {
        bondLedgerId: tenancy.bond_ledger_id,
        payload
      });

      const response = await fetch(
        buildApiUrl(`/bond-ledgers/${tenancy.bond_ledger_id}/transfers`),
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        }
      );

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: 'Failed to create bond transfer' }));
        throw new Error(error.message || 'Failed to create bond transfer');
      }

      const result = await response.json();
      console.log('[BondModal] Bond transfer created successfully:', result);

      // Refresh data after successful transfer
      await Promise.all([
        fetchBondTransactions(),
        fetchBondSummary()
      ]);

      // Notify parent component to refresh data (payments list, etc.)
      if (onDataUpdate) {
        await onDataUpdate();
      }

      // Reset form and close modal
      setTransferAmount('');
      setShowTransferModal(false);
      alert('Bond transfer created successfully!');
    } catch (error) {
      console.error('Error creating bond transfer:', error);
      alert(`Failed to create bond transfer: ${error.message}`);
    } finally {
      setCreatingTransfer(false);
    }
  };

  // Get bond balance and totals - updated to calculate from actual transactions
  const bondLedgerBalance = bondSummary?.balance?.amount ? (bondSummary.balance.amount / 100) : 0;

  // Calculate total deposits from actual transactions
  const totalDeposits = bondTransactions
    .filter(transaction => transaction.type === 'deposit')
    .reduce((total, transaction) => total + transaction.amount, 0);

  // Calculate total deductions from actual transactions (includes both deductions and transfers)
  const totalDeductions = bondTransactions
    .filter(transaction => transaction.type === 'deduction' || transaction.type === 'transfer')
    .reduce((total, transaction) => total + transaction.amount, 0);

  // Calculate total transfers from actual transactions
  const totalTransfers = bondTransactions
    .filter(transaction => transaction.type === 'transfer')
    .reduce((total, transaction) => total + transaction.amount, 0);

  const bondLedgerCurrency = bondSummary?.balance?.currency || agencyCurrency;
  const bondLedgerSymbol = getCurrencySymbol(bondLedgerCurrency);

  // Add debug logging for the bond data
  console.log('[BondModal] Processed bond data:', {
    rawSummary: bondSummary,
    bondLedgerBalance,
    transactionsCount: bondTransactions.length,
    totalDeposits,
    totalDeductions, // Now includes both deductions and transfers
    separateBreakdown: {
      onlyDeductions: bondTransactions.filter(t => t.type === 'deduction').reduce((total, t) => total + t.amount, 0),
      onlyTransfers: bondTransactions.filter(t => t.type === 'transfer').reduce((total, t) => total + t.amount, 0)
    },
    bondLedgerCurrency,
    transactions: bondTransactions
  });

  if (!show || !tenancy) return null;

  return (
    <>
      {/* Main Bond Modal */}
      <Transition appear show={show} as={Fragment}>
        <Dialog as="div" className="relative z-[1060]" onClose={onClose}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-5xl transform overflow-hidden rounded-2xl bg-white shadow-2xl transition-all">
                  {/* Header */}
                  <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 px-6 py-4">
                    <div className="flex items-center justify-between text-white">
                      <div className="flex items-center space-x-3">
                        <ShieldCheckIcon className="w-6 h-6" />
                        <div className="text-left">
                          <Dialog.Title as="h2" className="text-lg font-semibold">
                            Security Bond Management
                          </Dialog.Title>
                          <p className="text-emerald-100 text-sm">
                            {selectedOccupant ? `${selectedOccupant.person?.given || selectedOccupant.given || selectedOccupant.first_name || ''} ${selectedOccupant.person?.surname || selectedOccupant.surname || selectedOccupant.last_name || ''}`.trim() : 'Agreement'} -
                            Current Balance: {bondLedgerSymbol}{bondLedgerBalance.toFixed(2)}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={onClose}
                        className="p-2 hover:bg-white/20 rounded-lg transition-colors duration-200"
                      >
                        <XMarkIcon className="w-5 h-5" />
                      </button>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    {(loadingSummary || loadingTransactions) ? (
                      <div className="flex flex-col items-center justify-center py-12">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
                        <p className="mt-4 text-gray-600">Loading bond data...</p>
                      </div>
                    ) : (
                      <div className="space-y-6">
                        {/* Action Buttons */}
                        <div className="flex justify-end space-x-3">
                          <button
                            onClick={() => setShowDepositModal(true)}
                            className="inline-flex items-center px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white font-medium rounded-lg transition-colors duration-200 shadow-md hover:shadow-lg"
                          >
                            <PlusCircleIcon className="w-5 h-5 mr-2" />
                            Add Deposit
                          </button>
                          <button
                            onClick={() => setShowDeductionModal(true)}
                            className="inline-flex items-center px-4 py-2 bg-red-500 hover:bg-red-600 text-white font-medium rounded-lg transition-colors duration-200 shadow-md hover:shadow-lg"
                          >
                            <MinusCircleIcon className="w-5 h-5 mr-2" />
                            Add Deduction
                          </button>
                          <button
                            onClick={() => setShowTransferModal(true)}
                            className="inline-flex items-center px-4 py-2 bg-indigo-500 hover:bg-indigo-600 text-white font-medium rounded-lg transition-colors duration-200 shadow-md hover:shadow-lg"
                          >
                            <ArrowPathIcon className="w-5 h-5 mr-2" />
                            Bond Transfer
                          </button>
                        </div>

                        {/* Modern Stats Cards */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                          <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-2xl p-6 border border-emerald-200 shadow-lg hover:shadow-xl transition-shadow duration-300">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-emerald-600 text-sm font-medium">Total Deposits</p>
                                <p className="text-3xl font-bold text-emerald-900 mt-1">
                                  {bondLedgerSymbol}{totalDeposits.toFixed(2)}
                                </p>
                              </div>
                              <div className="p-3 bg-emerald-500 rounded-xl shadow-lg">
                                <ArrowTrendingUpIcon className="w-6 h-6 text-white" />
                              </div>
                            </div>
                          </div>

                          <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-2xl p-6 border border-red-200 shadow-lg hover:shadow-xl transition-shadow duration-300">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-red-600 text-sm font-medium">Total Deductions</p>
                                <p className="text-3xl font-bold text-red-900 mt-1">
                                  {bondLedgerSymbol}{totalDeductions.toFixed(2)}
                                </p>
                                <p className="text-sm text-red-500">Includes transfers</p>
                              </div>
                              <div className="p-3 bg-red-500 rounded-xl shadow-lg">
                                <ArrowTrendingDownIcon className="w-6 h-6 text-white" />
                              </div>
                            </div>
                          </div>

                          <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-6 border border-blue-200 shadow-lg hover:shadow-xl transition-shadow duration-300">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-blue-600 text-sm font-medium">Current Balance</p>
                                <p className="text-3xl font-bold text-blue-900 mt-1">
                                  {bondLedgerSymbol}{bondLedgerBalance.toFixed(2)}
                                </p>
                              </div>
                              <div className="p-3 bg-blue-500 rounded-xl shadow-lg">
                                <CurrencyDollarIcon className="w-6 h-6 text-white" />
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Transactions Table */}
                        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
                          <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                            <h3 className="text-lg font-semibold text-gray-900">Transaction History</h3>
                          </div>
                          {bondTransactions.length > 0 ? (
                            <div className="overflow-x-auto">
                              <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                  <tr>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-28">Date</th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">Type</th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-0">Description</th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-28">Reference</th>
                                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-32">Amount</th>
                                    <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-20">Actions</th>
                                  </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                  {bondTransactions.map((transaction) => (
                                    <tr key={transaction.id} className="hover:bg-gray-50 transition-colors duration-150">
                                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-left">
                                        <div className="font-medium text-left">
                                          {formatDateWithContext(transaction.date)}
                                        </div>
                                      </td>
                                      <td className="px-6 py-4 whitespace-nowrap text-left">
                                        <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${
                                          transaction.type === 'deposit' 
                                            ? 'bg-emerald-100 text-emerald-800'
                                            : transaction.type === 'deduction'
                                            ? 'bg-red-100 text-red-800' 
                                            : 'bg-indigo-100 text-indigo-800'
                                        }`}>
                                          {transaction.type === 'deposit' ? 'Deposit' :
                                           transaction.type === 'deduction' ? 'Deduction' : 'Transfer'}
                                        </span>
                                      </td>
                                      <td className="px-6 py-4 text-sm text-gray-900 text-left">
                                        <div className="max-w-xs truncate text-left" title={transaction.description}>
                                          {transaction.description}
                                        </div>
                                      </td>
                                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-left">
                                        <div className="max-w-24 truncate text-left" title={transaction.reference}>
                                          {transaction.reference || '-'}
                                        </div>
                                      </td>
                                      <td className="px-6 py-4 whitespace-nowrap text-right">
                                        <div className={`text-sm font-semibold tabular-nums ${
                                          transaction.type === 'deposit' ? 'text-emerald-600' : 
                                          transaction.type === 'deduction' ? 'text-red-600' :
                                          'text-indigo-600'
                                        }`}>
                                          <span className="mr-1">
                                            {transaction.type === 'deposit' ? '+' : '-'}
                                          </span>
                                          <span>
                                            {bondLedgerSymbol}{Math.abs(transaction.amount).toFixed(2)}
                                          </span>
                                        </div>
                                      </td>
                                      <td className="px-6 py-4 whitespace-nowrap text-center">
                                        <button
                                          onClick={() => handleDeleteTransaction(transaction)}
                                          disabled={deletingTransaction === transaction.id}
                                          className="inline-flex items-center p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                          title="Delete transaction"
                                        >
                                          {deletingTransaction === transaction.id ? (
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-500"></div>
                                          ) : (
                                            <TrashIcon className="w-4 h-4" />
                                          )}
                                        </button>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          ) : (
                            <div className="text-center py-12">
                              <CurrencyDollarIcon className="mx-auto h-12 w-12 text-gray-400" />
                              <h3 className="mt-2 text-sm font-medium text-gray-900">No transactions yet</h3>
                              <p className="mt-1 text-sm text-gray-500">Start by adding a deposit or deduction.</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Footer */}
                  <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
                    <div className="flex justify-end">
                      <button
                        onClick={onClose}
                        className="px-6 py-2 bg-white border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors duration-200 flex items-center"
                      >
                        <XMarkIcon className="w-4 h-4 mr-2" />
                        Close
                      </button>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Add Deposit Modal */}
      <Transition appear show={showDepositModal} as={Fragment}>
        <Dialog as="div" className="relative z-[1070]" onClose={() => setShowDepositModal(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-lg transform overflow-hidden rounded-2xl bg-white shadow-2xl transition-all">
                  {/* Header */}
                  <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 px-6 py-4">
                    <div className="flex items-center justify-between text-white">
                      <div className="flex items-center space-x-3">
                        <PlusCircleIcon className="w-6 h-6" />
                        <Dialog.Title as="h2" className="text-lg font-semibold">
                          Add Bond Deposit
                        </Dialog.Title>
                      </div>
                      <button
                        onClick={() => setShowDepositModal(false)}
                        className="p-2 hover:bg-white/20 rounded-lg transition-colors duration-200"
                      >
                        <XMarkIcon className="w-5 h-5" />
                      </button>
                    </div>
                  </div>

                  {/* Form */}
                  <form onSubmit={handleDepositSubmit} className="p-6 space-y-5">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Amount *</label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <span className="text-gray-500 text-sm">{bondLedgerSymbol}</span>
                          </div>
                          <input
                            type="number"
                            className={`block w-full pl-8 pr-4 py-2.5 border rounded-lg shadow-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors ${
                              errors.amount ? 'border-red-300' : 'border-gray-300'
                            }`}
                            value={depositForm.amount}
                            onChange={(e) => setDepositForm({...depositForm, amount: e.target.value})}
                            min="0"
                            step="0.01"
                            required
                            placeholder="0.00"
                          />
                          {errors.amount && <p className="mt-1 text-sm text-red-600">{errors.amount}</p>}
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Date *</label>
                        <input
                          type="date"
                          className={`block w-full px-4 py-2.5 border rounded-lg shadow-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors ${
                            errors.date ? 'border-red-300' : 'border-gray-300'
                          }`}
                          value={depositForm.date}
                          onChange={(e) => setDepositForm({...depositForm, date: e.target.value})}
                          onKeyDown={(e) => e.preventDefault()}
                          required
                        />
                        {depositForm.date && (
                          <p className="mt-1 text-xs font-semibold text-blue-600">{formatDateWithContext(depositForm.date)}</p>
                        )}
                        {errors.date && <p className="mt-1 text-sm text-red-600">{errors.date}</p>}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Reference/Transaction ID</label>
                      <input
                        type="text"
                        className="block w-full px-4 py-2.5 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors"
                        value={depositForm.reference}
                        onChange={(e) => setDepositForm({...depositForm, reference: e.target.value})}
                        placeholder="Optional reference number"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                      <textarea
                        className="block w-full px-4 py-2.5 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors"
                        value={depositForm.description}
                        onChange={(e) => setDepositForm({...depositForm, description: e.target.value})}
                        rows="2"
                        placeholder="Optional description..."
                      />
                    </div>

                    {/* Footer */}
                    <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                      <button
                        type="button"
                        onClick={() => setShowDepositModal(false)}
                        className="px-4 py-2 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors duration-200"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={savingTransaction}
                        className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 disabled:bg-gray-400 text-white font-medium rounded-lg transition-colors duration-200 flex items-center"
                      >
                        {savingTransaction ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                            Adding...
                          </>
                        ) : (
                          <>
                            <PlusCircleIcon className="w-4 h-4 mr-2" />
                            Add Deposit
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Add Deduction Modal */}
      <Transition appear show={showDeductionModal} as={Fragment}>
        <Dialog as="div" className="relative z-[1070]" onClose={() => setShowDeductionModal(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-lg transform overflow-hidden rounded-2xl bg-white shadow-2xl transition-all">
                  {/* Header */}
                  <div className="bg-gradient-to-r from-red-500 to-red-600 px-6 py-4">
                    <div className="flex items-center justify-between text-white">
                      <div className="flex items-center space-x-3">
                        <MinusCircleIcon className="w-6 h-6" />
                        <Dialog.Title as="h2" className="text-lg font-semibold">
                          Add Bond Deduction
                        </Dialog.Title>
                      </div>
                      <button
                        onClick={() => setShowDeductionModal(false)}
                        className="p-2 hover:bg-white/20 rounded-lg transition-colors duration-200"
                      >
                        <XMarkIcon className="w-5 h-5" />
                      </button>
                    </div>
                  </div>

                  {/* Form */}
                  <form onSubmit={handleDeductionSubmit} className="p-6 space-y-5">
                    {/* Current Balance Warning */}
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                      <div className="flex items-center">
                        <ExclamationTriangleIcon className="h-5 w-5 text-amber-500 mr-2 flex-shrink-0" />
                        <p className="text-sm text-amber-800">
                          <strong>Current Bond Balance:</strong> {bondLedgerSymbol}{bondLedgerBalance.toFixed(2)}
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Amount *</label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <span className="text-gray-500 text-sm">{bondLedgerSymbol}</span>
                          </div>
                          <input
                            type="number"
                            className={`block w-full pl-8 pr-4 py-2.5 border rounded-lg shadow-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors ${
                              errors.amount ? 'border-red-300' : 'border-gray-300'
                            }`}
                            value={deductionForm.amount}
                            onChange={(e) => {
                              setDeductionForm({...deductionForm, amount: e.target.value});
                              // Clear errors when user starts typing
                              if (errors.amount) {
                                setErrors({...errors, amount: ''});
                              }
                            }}
                            min="0"
                            max={bondLedgerBalance}
                            step="0.01"
                            required
                            placeholder="0.00"
                          />
                          {errors.amount && <p className="mt-1 text-sm text-red-600">{errors.amount}</p>}
                          {!errors.amount && (
                            <p className="mt-1 text-xs text-gray-500">
                              Maximum deduction: {bondLedgerSymbol}{bondLedgerBalance.toFixed(2)}
                            </p>
                          )}
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Date *</label>
                        <input
                          type="date"
                          className={`block w-full px-4 py-2.5 border rounded-lg shadow-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors ${
                            errors.date ? 'border-red-300' : 'border-gray-300'
                          }`}
                          value={deductionForm.date}
                          onChange={(e) => setDeductionForm({...deductionForm, date: e.target.value})}
                          onKeyDown={(e) => e.preventDefault()}
                          required
                        />
                        {deductionForm.date && (
                          <p className="mt-1 text-xs font-semibold text-blue-600">{formatDateWithContext(deductionForm.date)}</p>
                        )}
                        {errors.date && <p className="mt-1 text-sm text-red-600">{errors.date}</p>}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Reason *</label>
                      <select
                        className={`block w-full px-4 py-2.5 border rounded-lg shadow-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors ${
                          errors.reason ? 'border-red-300' : 'border-gray-300'
                        }`}
                        value={deductionForm.reason}
                        onChange={(e) => setDeductionForm({...deductionForm, reason: e.target.value})}
                        required
                      >
                        <option value="">
                          {loadingCategories ? 'Loading categories...' : 'Select a reason...'}
                        </option>
                        {chargeCategories.map(cat => (
                          <option key={cat.id} value={cat.id}>
                            {cat.display_name}
                          </option>
                        ))}
                      </select>
                      {errors.reason && <p className="mt-1 text-sm text-red-600">{errors.reason}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                      <textarea
                        className="block w-full px-4 py-2.5 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors"
                        value={deductionForm.description}
                        onChange={(e) => setDeductionForm({...deductionForm, description: e.target.value})}
                        rows="2"
                        placeholder="Details about the deduction..."
                      />
                    </div>

                    {/* Footer */}
                    <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                      <button
                        type="button"
                        onClick={() => setShowDeductionModal(false)}
                        className="px-4 py-2 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors duration-200"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={savingTransaction}
                        className="px-4 py-2 bg-red-500 hover:bg-red-600 disabled:bg-gray-400 text-white font-medium rounded-lg transition-colors duration-200 flex items-center"
                      >
                        {savingTransaction ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                            Adding...
                          </>
                        ) : (
                          <>
                            <MinusCircleIcon className="w-4 h-4 mr-2" />
                            Add Deduction
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Bond Transfer Modal */}
      <Transition appear show={showTransferModal} as={Fragment}>
        <Dialog as="div" className="relative z-[1070]" onClose={() => {
          setTransferAmount('');
          setShowTransferModal(false);
        }}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-lg transform overflow-hidden rounded-2xl bg-white shadow-2xl transition-all">
                  {/* Header */}
                  <div className="bg-gradient-to-r from-indigo-500 to-indigo-600 px-6 py-4">
                    <div className="flex items-center justify-between text-white">
                      <div className="flex items-center space-x-3">
                        <ArrowPathIcon className="w-6 h-6" />
                        <Dialog.Title as="h2" className="text-lg font-semibold">
                          Bond Transfer
                        </Dialog.Title>
                      </div>
                      <button
                        onClick={() => {
                          setTransferAmount('');
                          setShowTransferModal(false);
                        }}
                        className="p-2 hover:bg-white/20 rounded-lg transition-colors duration-200"
                      >
                        <XMarkIcon className="w-5 h-5" />
                      </button>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6 space-y-5">
                    {/* Current Balance Info */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <p className="text-sm text-blue-800">
                          <strong>Current Bond Balance:</strong>
                        </p>
                        <p className="text-lg font-bold text-blue-900">
                          {bondLedgerSymbol}{bondLedgerBalance.toFixed(2)}
                        </p>
                      </div>
                    </div>

                    {/* Transfer Amount Input */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Transfer Amount *</label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <span className="text-gray-500 text-sm">{bondLedgerSymbol}</span>
                        </div>
                        <input
                          type="number"
                          className="block w-full pl-8 pr-4 py-2.5 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                          value={transferAmount}
                          onChange={(e) => setTransferAmount(e.target.value)}
                          min="0"
                          max={bondLedgerBalance}
                          step="0.01"
                          placeholder="0.00"
                        />
                      </div>
                      <p className="mt-1 text-xs text-gray-500">
                        Maximum transfer: {bondLedgerSymbol}{bondLedgerBalance.toFixed(2)}
                      </p>
                    </div>

                    {/* Explanation */}
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                      <h4 className="font-semibold text-gray-900 mb-2">What is a Bond Transfer?</h4>
                      <p className="text-sm text-gray-600 mb-3">
                        A bond transfer allows you to use the security bond funds to pay outstanding rent.
                        This is typically used when:
                      </p>
                      <ul className="text-sm text-gray-600 list-disc list-inside space-y-1">
                        <li>The tenant has unpaid rent and insufficient funds</li>
                        <li>At the end of tenancy to settle remaining balances</li>
                        <li>When mutually agreed upon by landlord and tenant</li>
                      </ul>
                    </div>

                    {/* Warning */}
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                      <div className="flex">
                        <ExclamationTriangleIcon className="h-5 w-5 text-amber-500 mr-2 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm text-amber-800">
                            <strong>Important:</strong> Once a bond transfer is created, the specified amount
                            will be transferred from the bond to cover rent charges. This action
                            will be recorded in the transaction history.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
                    <div className="flex justify-end space-x-3">
                      <button
                        type="button"
                        onClick={() => {
                          setTransferAmount('');
                          setShowTransferModal(false);
                        }}
                        className="px-4 py-2 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors duration-200"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={handleCreateTransfer}
                        disabled={creatingTransfer || bondLedgerBalance <= 0 || !transferAmount || parseFloat(transferAmount) <= 0}
                        className="px-4 py-2 bg-indigo-500 hover:bg-indigo-600 disabled:bg-gray-400 text-white font-medium rounded-lg transition-colors duration-200 flex items-center"
                        title={bondLedgerBalance <= 0 ? 'No bond balance available for transfer' : !transferAmount ? 'Enter a transfer amount' : ''}
                      >
                        {creatingTransfer ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                            Creating...
                          </>
                        ) : (
                          <>
                            <ArrowPathIcon className="w-4 h-4 mr-2" />
                            Create Transfer
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
};

export default BondModal;
